import { ApolloProvider } from "@apollo/client";
import { BrowserRouter, Routes, Route } from "react-router";
import { apolloClient } from "./lib/apollo-client";
import { ThemeProvider } from "./context/ThemeContext";
import { LanguageProvider } from "./context/LanguageContext";
import { AuthProvider } from "./context/AuthContext";
import { OrgProvider } from "./context/OrgContext";
import { AdminRoute } from "./components/AdminRoute";
import { OrgSlugGuard } from "./components/OrgSlugGuard";
import { AppShell } from "./components/layout/AppShell";

// Public routes
import { LoginPage } from "./routes/login";
import { VerifyPage } from "./routes/verify";
import { UnauthorizedPage } from "./routes/unauthorized";
import { OidcLandingPage } from "./routes/oidc-landing";
import { OidcErrorPage } from "./routes/oidc-error";

// Org picker (root)
import { OrgPickerPage } from "./routes/org-picker";

// Protected routes (ORG_ADMIN + ADMIN) — org-scoped
import { DashboardPage } from "./routes/dashboard";
import { RosterPage } from "./routes/roster";
import { RewardsPage } from "./routes/rewards";
import { OrdersPage } from "./routes/orders";
import { ChallengesPage } from "./routes/challenges";
import { EventsPage } from "./routes/events";
import { ReportsPage } from "./routes/reports";
import { OrganizationSettingsPage } from "./routes/organization";
import { OrganizationSSOPage } from "./routes/sso";

// ADMIN-only routes (global, not org-scoped)
import { AdminOrganizationsPage } from "./routes/admin/organizations";
import { AdminUsersPage } from "./routes/admin/users";
import { AdminEconomyPage } from "./routes/admin/economy";
import { AdminRewardCatalogPage } from "./routes/admin/rewards";
import { AdminAnalyticsPage } from "./routes/admin/analytics";
import { AdminNotificationsPage } from "./routes/admin/notifications";
import { AdminQueuesPage } from "./routes/admin/queues";
import { AdminLeadsPage } from "./routes/admin/leads";
import { AdminHealthSyncPage } from "./routes/admin/health-sync";
import { AdminNotificationLogsPage } from "./routes/admin/notification-logs";

export function App() {
  return (
    <ApolloProvider client={apolloClient}>
      <ThemeProvider>
        <LanguageProvider>
        <BrowserRouter>
          <AuthProvider>
            <OrgProvider>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/verify" element={<VerifyPage />} />
                <Route path="/auth/oidc/landing" element={<OidcLandingPage />} />
                <Route path="/auth/oidc/error" element={<OidcErrorPage />} />
                <Route path="/unauthorized" element={<UnauthorizedPage />} />

                {/* Org picker — authenticated root */}
                <Route
                  index
                  element={
                    <AdminRoute>
                      <OrgPickerPage />
                    </AdminRoute>
                  }
                />

                {/* Org-scoped routes — /:orgSlug/* */}
                <Route
                  path=":orgSlug"
                  element={
                    <AdminRoute>
                      <OrgSlugGuard>
                        <AppShell />
                      </OrgSlugGuard>
                    </AdminRoute>
                  }
                >
                  <Route index element={<DashboardPage />} />
                  <Route path="settings" element={<OrganizationSettingsPage />} />
                  {import.meta.env.VITE_FEATURE_SSO_OIDC === "true" && (
                    <Route path="sso" element={<OrganizationSSOPage />} />
                  )}
                  <Route path="roster" element={<RosterPage />} />
                  <Route path="rewards" element={<RewardsPage />} />
                  <Route path="orders" element={<OrdersPage />} />
                  <Route path="challenges" element={<ChallengesPage />} />
                  <Route path="events" element={<EventsPage />} />
                  <Route path="reports" element={<ReportsPage />} />

                  {/* TODO: Entity-level deep links for future use
                  <Route path="orders/:orderId" element={<OrderDetailPage />} />
                  <Route path="challenges/:challengeId" element={<ChallengeDetailPage />} />
                  <Route path="events/:eventId" element={<EventDetailPage />} />
                  <Route path="roster/:memberId" element={<MemberDetailPage />} />
                  */}
                </Route>

                {/* Global admin routes (ADMIN only, not org-scoped) */}
                <Route
                  element={
                    <AdminRoute allowedRoles={["ADMIN"]}>
                      <AppShell />
                    </AdminRoute>
                  }
                >
                  <Route path="admin/organizations" element={<AdminOrganizationsPage />} />
                  <Route path="admin/users" element={<AdminUsersPage />} />
                  <Route path="admin/economy" element={<AdminEconomyPage />} />
                  <Route path="admin/rewards" element={<AdminRewardCatalogPage />} />
                  <Route path="admin/analytics" element={<AdminAnalyticsPage />} />
                  <Route path="admin/notifications" element={<AdminNotificationsPage />} />
                  <Route path="admin/queues" element={<AdminQueuesPage />} />
                  <Route path="admin/leads" element={<AdminLeadsPage />} />
                  <Route path="admin/health-sync" element={<AdminHealthSyncPage />} />
                  <Route path="admin/notification-logs" element={<AdminNotificationLogsPage />} />
                </Route>
              </Routes>
            </OrgProvider>
          </AuthProvider>
        </BrowserRouter>
        </LanguageProvider>
      </ThemeProvider>
    </ApolloProvider>
  );
}
